import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { useProjectRooms } from './useProjectRooms';

const hintFor = (s) => {
  if (s.source === 'clips') return ` (from your clips · ${s.count || 1})`;
  if (s.source === 'estimate') return ' (from the estimate)';
  return ' (from the Takeoff)';
};

/**
 * SP-3 — the one room picker. `value` is {id, name} (a bare string is read as a
 * name). `onChange` receives {id, name, room} — always a real room when the
 * project has one: picking a suggestion or typing a new name creates the room
 * first. With no project it is a plain name field and id stays null.
 */
export default function RoomPicker({
  projectId, value, onChange, testid = 'room-picker', className = 'ksp__field',
  disabled = false, placeholder = 'Select room…',
}) {
  const { rooms, suggestions, createRoom } = useProjectRooms(projectId);
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);

  const given = typeof value === 'string' ? { id: null, name: value } : (value || { id: null, name: '' });
  const byId = given.id ? rooms.find((r) => r.id === given.id) : null;
  const byName = !byId && given.name ? rooms.find((r) => r.name === given.name) : null;
  const current = byId || byName || given;

  const add = async (text, suggestion = null) => {
    setBusy(true);
    const room = await createRoom(text, suggestion);
    setBusy(false);
    if (room) {
      onChange({ id: room.id, name: room.name, room });
      setCreating(false);
      setName('');
    }
    return room;
  };

  if (!projectId) {
    return (
      <input className={className} value={current.name || ''} disabled={disabled}
        placeholder="Room name"
        onChange={(e) => onChange({ id: null, name: e.target.value, room: null })}
        data-testid={testid} />
    );
  }

  if (creating) {
    return (
      <span className="flex items-center gap-1">
        <input className={className} value={name} autoFocus placeholder="Room name"
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') { setCreating(false); setName(''); }
            if (e.key === 'Enter' && name.trim() && !busy) { e.preventDefault(); add(name.trim()); }
          }}
          data-testid={`${testid}-new-name`} />
        <Button type="button" size="sm" variant="outline" disabled={busy || !name.trim()}
          onClick={() => add(name.trim())} data-testid={`${testid}-new-save`}>Add</Button>
        <Button type="button" size="sm" variant="ghost" disabled={busy}
          onClick={() => { setCreating(false); setName(''); }}
          data-testid={`${testid}-new-cancel`}>Cancel</Button>
      </span>
    );
  }

  const selected = current.id ? `id:${current.id}` : (current.name ? `name:${current.name}` : '');
  const onList = Boolean(current.id)
    || (current.name && suggestions.some((s) => s.name === current.name));

  return (
    <select className={className} value={selected} disabled={disabled || busy} data-km-noprint
      data-testid={testid}
      onChange={async (e) => {
        const v = e.target.value;
        if (v === '__new__') { setCreating(true); return; }
        if (!v) { onChange({ id: null, name: '', room: null }); return; }
        if (v.startsWith('id:')) {
          const r = rooms.find((x) => x.id === v.slice(3));
          if (r) onChange({ id: r.id, name: r.name, room: r });
          return;
        }
        const s = suggestions.find((x) => x.name === v.slice(5));
        if (s) await add(s.name, s);
      }}>
      <option value="">{placeholder}</option>
      {current.name && !onList ? (
        <option value={selected}>{current.name} (not on the list)</option>
      ) : null}
      {rooms.map((r) => (
        <option key={r.id} value={`id:${r.id}`}>{r.name}</option>
      ))}
      {suggestions.length ? (
        <optgroup label="Seen, not yet a room">
          {suggestions.map((s) => (
            <option key={`s-${s.name}`} value={`name:${s.name}`}>{s.name}{hintFor(s)}</option>
          ))}
        </optgroup>
      ) : null}
      <option value="__new__">+ New room…</option>
    </select>
  );
}
